import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRightIcon } from '@heroicons/react/24/outline'
import { cx } from './primitives'
import './dashboard.css'

export interface FolderStatCardProps {
  /** Small uppercase label shown inside the folder tab. */
  label: string
  /** accent = solid brand orange (featured), muted = light grey, white, dark. */
  tone?: 'accent' | 'muted' | 'white' | 'dark'
  /** Big number (string/number) — rendered in the middle area. */
  value?: React.ReactNode
  /** Small unit after the big number. */
  unit?: React.ReactNode
  /** Custom middle visual instead of a number (avatar stack, ring…). */
  visual?: React.ReactNode
  /** Bottom caption (uppercase) + accent value line. */
  caption?: React.ReactNode
  captionValue?: React.ReactNode
  /** Optional link: shows a round arrow button bottom-right. */
  to?: string
  /** Optional element top-right (chip, delta…). */
  corner?: React.ReactNode
  loading?: boolean
  className?: string
}

/**
 * Stat card shaped like a file folder: rounded body with a raised tab on the
 * top-left edge (CSS pseudo-elements + an inverted-radius fillet so the
 * silhouette is one continuous shape; the wrapper's drop-shadow follows it).
 */
/** Tab width follows the label length so long labels never truncate. */
export const tabWidth = (label: string): React.CSSProperties => ({ ['--tab-w' as any]: `min(78%, max(40%, ${Math.round(label.length * 7.9 + 56)}px))` })

export function FolderStatCard({ label, tone = 'muted', value, unit, visual, caption, captionValue, to, corner, loading, className }: FolderStatCardProps) {
  return (
    <div className={cx('sf-folder-wrap sf-fade', className)}>
      <div className={cx('sf-folder', tone !== 'muted' && tone)} style={tabWidth(label)}>
        <div className="sf-folder-label">{label}</div>
        {corner && <div className="sf-folder-corner">{corner}</div>}
        <div className="sf-folder-mid">
          {loading ? (
            <div className="sf-skel" style={{ width: '55%', height: 46, opacity: tone === 'accent' ? 0.4 : 1 }} />
          ) : visual ?? (
            <div className="sf-folder-big">{value ?? '—'}{unit && <small>{unit}</small>}</div>
          )}
        </div>
        <div className="sf-folder-foot">
          <div style={{ minWidth: 0 }}>
            {caption && <div className="sf-folder-cap">{caption}</div>}
            {captionValue !== undefined && <div className="sf-folder-capv">{loading ? '…' : captionValue}</div>}
          </div>
          {to && (
            <Link to={to} className="sf-folder-arrow" aria-label={`Open ${label.toLowerCase()}`}>
              <ArrowUpRightIcon />
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

/** Folder variant with a thin vertical accent bar, an icon, a linked title and a "LAST UPDATED" line. */
export function FolderUpdateCard({ label, icon, title, to, onClick, updatedAt, emptyText = 'Nothing yet', tone = 'muted', className }: {
  label: string
  icon: React.ReactNode
  title?: React.ReactNode
  to?: string
  onClick?: () => void
  updatedAt?: string | null
  emptyText?: string
  tone?: 'muted' | 'white'
  className?: string
}) {
  const date = updatedAt ? new Date(updatedAt) : null
  const titleEl = title
    ? to ? <Link to={to} className="sf-folder-bar-title">{title}</Link>
      : onClick ? <button type="button" onClick={onClick} className="sf-folder-bar-title" style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', color: 'inherit', textAlign: 'left', fontFamily: 'inherit' }}>{title}</button>
      : <span className="sf-folder-bar-title">{title}</span>
    : <span style={{ color: 'var(--sf-mid)', fontWeight: 500 }}>{emptyText}</span>
  return (
    <div className={cx('sf-folder-wrap sf-fade', className)}>
      <div className={cx('sf-folder', tone === 'white' && 'white')} style={tabWidth(label)}>
        <div className="sf-folder-label">{label}</div>
        <div className="sf-folder-bar">
          <span className="sf-folder-bar-line" />
          <div className="sf-folder-bar-body">
            <span className="sf-folder-bar-icon">{icon}</span>
            {titleEl}
          </div>
        </div>
        <div className="sf-folder-foot">
          <div>
            <div className="sf-folder-cap">Last updated</div>
            <div className="sf-folder-capv">
              {date && !isNaN(date.getTime())
                ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
